import { FileUploader, Input, Switch, Textarea } from '@cokitchen/cokitchen-components';
import React, { useEffect, useState } from 'react';
import { DeleteIcon } from 'cokitchen-icons';
import { BrandOption } from 'utils/types';
import useMealContext from 'contexts/meal.context';
import { generateSlug, isEditMeal } from 'utils/functions';
import { useLocation } from 'react-router-dom';
import SelectMeal from 'components/search-meal';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { STORAGE_KEYS } from 'utils/constants';
import SelectBrand from 'components/select-brand';
import Select from 'components/base/select';
import { ENUM_BRAND_TYPE } from 'utils/enum';

const MealDetails = () => {
  const MAX_UPLOAD = 6;
  const { pathname } = useLocation();
  const { formData, setFormData, setDisabled } = useMealContext();
  const [isChecked, setIsChecked] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandOption>();
  const [selectedMeal, setSelectedMeal] = useState<BrandOption[]>([]);
  const [isActive, setIsActive] = useState(isEditMeal(pathname) ? formData.active : true);
  const [isNew, setIsNew] = useState(isEditMeal(pathname) ? formData.new : true);
  const [isAddon, setIsAddon] = useState(isEditMeal(pathname) ? formData.is_addon : false);
  const [isAlcohol, setIsAlcohol] = useState(isEditMeal(pathname) ? formData.alcohol : false);
  const [imageLimit, setImageLimit] = useState(false);
  const [slugError, setSlugError] = useState('');
  const { baseMealList, selectedBaseMeal, setSelectedBaseMeal, allBrands, meal } = useMealContext();

  const slugPattern = /^[a-z0-9-]+$/;

  const handleSetImageUrls = (urls: string[]) => {
    if (urls.length === 0) {
      setFormData({
        ...formData,
        images: []
      });
      setImageLimit(false);
      return;
    }

    const allImageUrls = formData?.images?.length ? [...formData.images, ...urls] : urls;

    if (allImageUrls.length > MAX_UPLOAD) {
      setImageLimit(true);
      return;
    }

    setFormData({
      ...formData,
      images: allImageUrls
    });
    setImageLimit(false);
  };

  useEffect(() => {
    if (selectedMeal.length) {
      setFormData({
        ...formData,
        images: selectedMeal[0]?.images
      });
    }
  }, [selectedMeal]);

  useEffect(() => {
    setSelectedMeal([]);
  }, [selectedBrand]);

  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
  useEffect(() => {
    if (formData.name && formData.description && formData.amount && formData.preparation_time) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [formData]);

  useEffect(() => {
    setFormData({
      ...formData,
      active: isActive,
      new: isNew,
      is_addon: isAddon,
      alcohol: isAlcohol
    });
  }, [isActive, isNew, isAddon, isAlcohol]);

  const handleUpdateImage = (url: string, index: number) => {
    const images = [...formData.images];
    images[index] = url;
    setFormData({ ...formData, images });
  };

  const handleDeleteImage = (index: number) => {
    const images = [...formData.images];
    images[index] = '';
    setFormData({ ...formData, images });
  };

  const isEditMealPage = isEditMeal(pathname);
  useEffect(() => {
    const selectedBaseMealIds = selectedBaseMeal.map((meal) => meal.value);
    setFormData({
      ...formData,
      base_meal_id: selectedBaseMealIds
    });
  }, [selectedBaseMeal, selectedBrand]);

  useEffect(() => {
    if (!formData.slug) {
      setSlugError('');
      return;
    }

    if (!slugPattern.test(formData.slug)) {
      setSlugError('Slug can only contain lowercase letters, numbers, and hyphens.');
    } else {
      setSlugError('');
    }
  }, [formData.slug]);

  const mealSlug = generateSlug(formData.name ?? '');
  return (
    <div className='flex flex-col space-y-4'>
      <div className='mb-6'>
        <p className='text-sm font-medium mb-2'>Select Base Meal</p>
        <Select
          hasSearch
          placeholder='Select Base Meal'
          value={selectedBaseMeal.map((c) => c.value)?.[0]}
          options={baseMealList}
          onChange={(v) => setSelectedBaseMeal(baseMealList.filter((c) => c.value === v))}
        />
      </div>
      <Input
        label='Meal Name'
        placeholder='Enter meal name'
        value={formData.name}
        onChange={(e) => {
          const name = e.target.value;

          setFormData({
            ...formData,
            name,
            slug: mealSlug
          });
        }}
      />
      <Input
        className='my-3'
        label='Meal Slug'
        placeholder='e.g. smoked-jollof-rice'
        value={formData.slug}
        onChange={(e) => {
          const slug = e.target.value;
          setFormData({
            ...formData,
            slug
          });
        }}
      />
      {slugError && <p className='text-red-5 text-sm mt-1'>{slugError}</p>}

      <Textarea
        label='Meal Description'
        placeholder='Describe the brand'
        rows={4}
        value={formData.description}
        onChange={(e) => {
          setFormData({
            ...formData,
            description: e.target.value
          });
        }}
      />
      <div className='relative'>
        <Input
          label='Meal Amount'
          type='number'
          placeholder='Enter meal amount'
          pattern='[0-9]*'
          value={formData.amount}
          onChange={(e) => {
            setFormData({
              ...formData,
              amount: e.target.value
            });
          }}
          onWheel={(e) => e.currentTarget.blur()}
        />
        <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-12 right-4 z-50'>
          <p className='text-xs'>NGN</p>
        </div>
      </div>
      <Input
        label='Available No.'
        placeholder='Enter available no.'
        value={formData.available_no}
        onChange={(e) => {
          setFormData({
            ...formData,
            available_no: e.target.value
          });
        }}
      />
      <div className='bg-[#FEF7E1] rounded-md p-[10px] flex items-center gap-[8px] text-[#BD8F05] mt-4'>
        <InfoIcon />
        <p className='text-xs text-[#85806F] font-medium'>When left blank, INFINITE will be used</p>
      </div>
      <Input
        label='Minimum Age'
        placeholder='Enter minimum age'
        value={formData.minimum_age}
        type='number'
        min={0}
        max={99}
        onChange={(e) => {
          setFormData({
            ...formData,
            minimum_age: e.target.value.toString()
          });
        }}
      />

      <div className='relative'>
        <Input
          label='Calories'
          type='tel'
          placeholder='Enter calories'
          value={formData.calories}
          pattern='[0-9]*'
          onChange={(e) => {
            setFormData({
              ...formData,
              calories: e.target.value
            });
          }}
        />

        <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-12 right-4 z-50'>
          <p className='text-xs'>KCAL</p>
        </div>
      </div>
      <div className='my-2 bg-[#F9F6F6] grid grid-cols-2 gap-y-3 gap-x-5 rounded-md border-[0.5px] border-[#EEECEC] px-[20px] py-3'>
        <Input
          type='checkbox'
          label='Is this meal new?'
          checked={formData.new}
          inputClass='font-medium text-xl'
          onChange={(e) => {
            setIsNew(e.target.checked);
          }}
        />
        <Input
          type='checkbox'
          label='Is this meal active?'
          checked={isActive}
          inputClass='font-medium text-xl'
          onChange={() => {
            setIsActive(!isActive);
          }}
        />
        <Input
          type='checkbox'
          label='Is this meal an addon?'
          checked={isAddon}
          inputClass='font-medium text-xl'
          onChange={() => {
            setIsAddon(!isAddon);
          }}
        />
        <Input
          type='checkbox'
          label='Is this meal an alcohol?'
          checked={isAlcohol}
          inputClass='font-medium text-xl'
          onChange={() => {
            setIsAlcohol(!isAlcohol);
          }}
        />
      </div>
      <div className='relative'>
        <Input
          label='Meal preparation time'
          type='tel'
          placeholder='Enter duration'
          pattern='[0-9]*'
          value={formData.preparation_time}
          onChange={(e) => {
            setFormData({
              ...formData,
              preparation_time: e.target.value
            });
          }}
        />
        <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-12 right-4 z-50'>
          <p className='text-xs'>MINS</p>
        </div>
      </div>
      <div className='flex gap-6'>
        {(allBrands.find((brand) => brand.id === formData.brand_id[0])?.brand_type ===
          ENUM_BRAND_TYPE.BUDGET ||
          meal.brand?.brand_type === ENUM_BRAND_TYPE.BUDGET) && (
          <Switch
            label='Mark as Budget Combo'
            active={formData.budget_combo}
            labelPosition='right'
            className='mb-2'
            onChange={(e) => {
              setFormData({
                ...formData,
                budget_combo: e
              });
            }}
            switchClassName='bg-red-5'
          />
        )}
        <Switch
          label='Use image from an already existing meal'
          active={isChecked}
          labelPosition='right'
          className='mb-2'
          onChange={() => {
            setIsChecked(!isChecked);
          }}
          switchClassName='bg-red-5'
        />
      </div>
      {isEditMealPage ? (
        <div className='border border-[#EAEBEB] rounded-xl mt-6 '>
          <div className='px-6 py-[18px]'>
            <p className=' text-[#1A1A1A] text-sm  font-medium '>Meal Image (optional)</p>
            <p className='text-[#595959] text-xs'>Upload up to 6 images</p>
          </div>
          <hr className='text-[#EAEBEB]' />
          <div className='p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
            {formData?.images?.map((url: string, index: number) => (
              <div key={`${url}${index}`} className='relative group '>
                <FileUploader
                  token={token}
                  fileUrls={url ? [url] : []}
                  setFileUrls={(urls) => handleUpdateImage(urls[0], index)}
                  className='h-[160px]'
                />

                {url ? (
                  <div
                    onClick={() => handleDeleteImage(index)}
                    className='absolute top-[14px] right-5 cursor-pointer opacity-0 group-hover:opacity-100 '>
                    <DeleteIcon className='text-[#fff]' />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <FileUploader
            token={token}
            fileUrls={formData?.images?.length ? formData.images : []}
            setFileUrls={handleSetImageUrls}
            label={
              <div className='mb-2'>
                <h3 className='font-medium'>Meal Image (optional)</h3>
                <p className='text-sm text-[#595959]'>Upload up to 6 images</p>
              </div>
            }
            isMultiple
            disabled={formData?.images?.length >= MAX_UPLOAD}
          />
          {imageLimit && (
            <p className='text-sm text-red-5 mb-4'>You cannot upload more than 6 images</p>
          )}
        </>
      )}

      <div className='mt-8 bg-[#F9F6F6] rounded-md border-[0.5px] border-[#EEECEC] px-[20px] py-3'>
        <Switch
          label='Use image from an already existing meal'
          active={isChecked}
          labelPosition='right'
          className='mb-2'
          onChange={() => {
            setIsChecked(!isChecked);
          }}
          switchClassName='bg-red-5'
        />
        {isChecked && (
          <div className='mt-2 border-t-[0.4px] border-t-[#DDDEDF] pt-4 flex flex-col space-y-4'>
            <SelectBrand
              brands={selectedBrand ? [selectedBrand] : []}
              setBrands={([b]) => setSelectedBrand(b)}
            />
            <SelectMeal
              selectedBrand={selectedBrand ? [selectedBrand] : []}
              selectedMeal={selectedMeal}
              setSelectedMeal={setSelectedMeal}
              hasSearch={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MealDetails;
